import {
  Directive,
  ElementRef,
  Input,
  OnInit,
  OnChanges,
  AfterViewInit,
  SimpleChanges,
  Renderer2, OnDestroy
} from '@angular/core';
import * as lazySizes from 'lazysizes';
import { botDetector } from '~app/modules/generic/helpers';

const overFlowXPropertyName = 'overflow-x';

@Directive({
  selector: '[appLazyLoadImage]'
})
export class LazyLoadImageDirective implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() appLazyLoadImage: string;
  @Input() srcSet: string;

  loadingElement: HTMLElement;
  imageWrapper: HTMLElement;
  private nativeElementUnlisten: () => void = () => ({});

  constructor(private el: ElementRef,
              private renderer: Renderer2) {
  }

  ngOnInit(): void {
    if (lazySizes) {
      lazySizes.init();
    }
  }

  ngAfterViewInit() {
    this.loadingElement = this.renderer.createElement('div');

    this.imageWrapper = this.renderer.parentNode(this.el.nativeElement);

    // adding loading element to the imageWrapper
    this.renderer.insertBefore(this.imageWrapper, this.loadingElement, this.imageWrapper.firstChild);

    this.addOverflowStyle();
    this.addPositionStyle();
    this.styleLoadingElement();

    // adding lazyload class to the img element
    this.renderer.addClass(this.el.nativeElement, 'lazyload');
  }

  ngOnChanges(changes: SimpleChanges) {
    const element = this.el.nativeElement;

    if ('src' in element && 0 < element.src.length) {
      return;
    }

    if (changes.hasOwnProperty('appLazyLoadImage')) {
      // Delete added loading element div when image is loaded
      this.nativeElementUnlisten = this.renderer.listen(this.el.nativeElement, 'load', () => {
        this.renderer.removeChild(this.imageWrapper, this.loadingElement);
        this.removeOverflowStyle();
        this.removePositionStyle();
      });

      const isBot = botDetector(navigator.userAgent);
      this.setImageSource(isBot);
      this.setImageSourceSet(isBot);
    }
  }

  private styleLoadingElement(): void {

    const width = Math.max(this.el.nativeElement.width, this.imageWrapper.offsetWidth);
    const height = Math.max(this.el.nativeElement.height, this.imageWrapper.offsetHeight);

    this.renderer.setStyle(this.loadingElement, 'position', 'absolute');
    this.renderer.setStyle(this.loadingElement, 'top', '0');
    this.renderer.setStyle(this.loadingElement, 'left', '0');
    this.renderer.setStyle(this.loadingElement, 'display', 'block');
    this.renderer.setStyle(this.loadingElement, 'height', `${height}px`);
    this.renderer.setStyle(this.loadingElement, 'width', `${width}px`);
    //TODO in the future, if we faced any case that has different background color, we should add it as input
    this.renderer.setStyle(this.loadingElement, 'background', '#fff');
    this.renderer.setStyle(this.loadingElement, 'z-index', '9');
  }

  private addOverflowStyle(): void {
    this.renderer.setStyle(this.el.nativeElement, 'display', 'block');
    this.renderer.setStyle(this.el.nativeElement, overFlowXPropertyName, 'hidden');
  }

  private removeOverflowStyle(): void {
    this.renderer.removeStyle(this.el.nativeElement, 'display');
    this.renderer.removeStyle(this.el.nativeElement, overFlowXPropertyName);
  }

  private addPositionStyle(): void {
    this.renderer.setStyle(this.imageWrapper, 'position', 'relative');
  }

  private removePositionStyle(): void {
    this.renderer.removeStyle(this.imageWrapper, 'position');
  }

  private setImageSource(isBot: boolean) {
    const attributeName = isBot ? 'src' : 'data-src';
    this.renderer.setAttribute(this.el.nativeElement, attributeName, this.appLazyLoadImage);
  }

  private setImageSourceSet(isBot: boolean) {
    // `srcSet` directive input isn't available across all usages of this directive
    if (!this.srcSet) {
      return;
    }
    const attributeName = isBot ? 'srcset' : 'data-srcset';
    this.renderer.setAttribute(this.el.nativeElement, attributeName, this.srcSet);
  }

  public ngOnDestroy() {
    this.nativeElementUnlisten();
  }
}
